import { defineStore } from 'pinia';
// import { TenantService } from '../services/TenantService'; // Mock
import { FirebaseTenantService } from '../services/FirebaseTenantService'; // Real

/**
 * Apply tenant theme CSS variables to document root
 * Maps styleOverrides from Firestore to CSS custom properties
 */
export function applyThemeToDOM(styleOverrides) {
    if (!styleOverrides) return;

    const root = document.documentElement;

    // 1. Legacy/Alias Mapping (For backward compatibility with named keys)
    const tokenMap = {
        primaryColor: ['--lds-primary', '--color-primary'],
        secondaryColor: ['--lds-secondary', '--color-secondary'],
        accentColor: ['--lds-accent', '--color-accent'],
        backgroundColor: ['--lds-bg-main', '--color-background'],
        surfaceColor: ['--lds-bg-card', '--color-surface'],
        textPrimary: ['--lds-text-main', '--color-text-primary'],
        textSecondary: ['--lds-text-secondary', '--color-text-secondary'],
    };

    Object.entries(tokenMap).forEach(([key, cssVars]) => {
        if (styleOverrides[key]) {
            const vars = Array.isArray(cssVars) ? cssVars : [cssVars];
            vars.forEach(v => {
                root.style.setProperty(v, styleOverrides[key]);
            });
        }
    });

    // 2. Generic Variable Injection (Sprint 4.1 - Single Truth)
    // Any key starting with '--' is injected directly to document root
    Object.entries(styleOverrides).forEach(([key, value]) => {
        if (key.startsWith('--')) {
            root.style.setProperty(key, value);
            console.debug(`[Theme] Generic Injection: ${key} = ${value}`);
        }
    });

    // 3. Special Tokens (Radius/Density)
    if (styleOverrides.borderRadius) {
        const radiusMap = { sm: '4px', md: '8px', lg: '12px', xl: '16px' };
        root.style.setProperty('--radius-tenant', radiusMap[styleOverrides.borderRadius] || '8px');
    }

    if (styleOverrides.density) {
        const densityMap = { compact: '0.75', comfortable: '1', spacious: '1.25' };
        root.style.setProperty('--density-tenant', densityMap[styleOverrides.density] || '1');
    }

    console.debug('[Theme] Theme hydration complete');
}

export const useTenantStore = defineStore('tenant', {
    state: () => ({
        currentTenant: null,
        isLoading: false,
        error: null
    }),

    getters: {
        tenantName: (state) => state.currentTenant?.tenantId || 'Unknown',
        activeTheme: (state) => state.currentTenant?.themeId,
        config: (state) => state.currentTenant?.features || {},
        regions: (state) => {
            const features = state.currentTenant?.features || {};
            // Priority: Explicit Regions (DB) > Feature Flags (Polyfill)
            if (state.currentTenant?.regions) return state.currentTenant.regions;

            // Polyfill for Legacy Tenants
            const main = [];

            if (features.showHero || features.enableGenerative) {
                // Header is required for Auth/Guest entry point in both cases
                main.push({ id: 'HeaderIsland', props: {} });
            }

            if (features.showHero) {
                main.push({
                    id: 'HeroIsland',
                    props: { title: (state.currentTenant?.name || 'Welcome'), subtitle: state.currentTenant?.tagline }
                });
            }

            if (features.enableGenerative) {
                main.push({
                    id: 'UploadIsland',
                    wrapper: { title: 'Step 1: Upload Your Photos' },
                    props: {}
                });
                main.push({
                    id: 'EmojiIsland',
                    wrapper: { title: 'Step 2: Choose Your New Expression' },
                    props: {}
                });
                main.push({
                    id: 'PreviewIsland',
                    wrapper: { title: 'Step 3: Transformation Preview' },
                    props: {}
                });
                main.push({
                    id: 'CollageIsland',
                    wrapper: { title: 'Step 4: Real-Time Match' },
                    props: {}
                });
                main.push({
                    id: 'CustomizerIsland',
                    wrapper: { title: '' },
                    props: { title: 'Step 5: Customize & Download' }
                });
            } else {
                // AuthIsland removed per user request
                // main.push({ id: 'AuthIsland', props: {} });
            }

            return { main };
        }
    },

    actions: {
        async initializeApp() {
            this.isLoading = true;
            this.error = null;
            try {
                const hostname = window.location.host;
                // const tenant = await TenantService.resolveTenant(hostname);
                const tenant = await FirebaseTenantService.resolveTenant(hostname);

                // --- LIBRARY RESOLUTION (Sprint 3.3) ---
                if (tenant && tenant.features) {
                    // Helper to fetch library content
                    const hydrateLibrary = async (libId) => {
                        if (!libId) return null;
                        try {
                            // We import db dynamically or assume similar access pattern as Service
                            // Since this is a store, we might not have direct DB access functions here.
                            // Ideally we should move this to TenantService, but for speed we do inline fetch or Service call.

                            // We will delegate to FirebaseTenantService to fetch library
                            return await FirebaseTenantService.getLibrary(libId);
                        } catch (e) {
                            console.warn(`Failed to hydration library ${libId}`, e);
                            return null;
                        }
                    };

                    // 1. Animals Library
                    if (tenant.features.animalsLibraryId) {
                        const lib = await hydrateLibrary(tenant.features.animalsLibraryId);
                        if (lib && lib.items) {
                            tenant.features.animals = lib.items;
                        }
                    }

                    // 2. Emotions Library
                    if (tenant.features.emojisLibraryId) {
                        const lib = await hydrateLibrary(tenant.features.emojisLibraryId);
                        if (lib && lib.items) {
                            tenant.features.emojis = lib.items;
                        }
                    }
                }
                // ----------------------------------------

                this.currentTenant = tenant;

                // Sentry tag tenant info for observability
                if (tenant && tenant.tenantId) {
                    import('@sentry/vue').then(Sentry => {
                        Sentry.setTag('tenant', tenant.tenantId);
                        Sentry.setTag('subdomain', hostname);
                    });
                }

                // Apply theme CSS variables to DOM (Sprint 4 - Theming)
                if (tenant?.features?.styleOverrides) {
                    applyThemeToDOM(tenant.features.styleOverrides);
                }
            } catch (err) {
                console.error('Failed to initialize tenant:', err);
                this.error = err.message;
            } finally {
                this.isLoading = false;
            }
        },

        /**
         * Explicitly set tenant and apply the associated theme
         * Useful for testing and runtime switching
         */
        setTenant(tenant) {
            this.currentTenant = tenant;
            if (tenant?.features?.styleOverrides) {
                applyThemeToDOM(tenant.features.styleOverrides);
            }
        }
    }
});

